import type { ReactNode } from "react";
import { useSceneStore, useTemporalStore } from "../store/sceneStore";
import {
  MeasureIcon,
  MoveIcon,
  RedoIcon,
  RotateIcon,
  ScaleIcon,
  SelectIcon,
  UndoIcon,
} from "./icons";
import type { TransformMode } from "../types";

export function Toolbar() {
  const interactionMode = useSceneStore((s) => s.interactionMode);
  const setInteractionMode = useSceneStore((s) => s.setInteractionMode);
  const transformMode = useSceneStore((s) => s.transformMode);
  const setTransformMode = useSceneStore((s) => s.setTransformMode);
  const selectedId = useSceneStore((s) => s.selectedId);

  const undo = useTemporalStore((s) => s.undo);
  const redo = useTemporalStore((s) => s.redo);
  const pastLength = useTemporalStore((s) => s.pastStates.length);
  const futureLength = useTemporalStore((s) => s.futureStates.length);

  const transformButtons: { mode: TransformMode; label: string; icon: typeof MoveIcon }[] = [
    { mode: "translate", label: "Verschieben", icon: MoveIcon },
    { mode: "rotate", label: "Drehen", icon: RotateIcon },
    { mode: "scale", label: "Skalieren", icon: ScaleIcon },
  ];

  return (
    <div className="icon-toolbar">
      <div className="icon-toolbar-group">
        <IconButton
          title="Auswahl"
          active={interactionMode === "select"}
          onClick={() => setInteractionMode("select")}
        >
          <SelectIcon />
        </IconButton>
        <IconButton
          title="Messen"
          active={interactionMode === "measure"}
          onClick={() => setInteractionMode("measure")}
        >
          <MeasureIcon />
        </IconButton>
      </div>

      <div className="icon-toolbar-divider" />

      <div className="icon-toolbar-group">
        {transformButtons.map(({ mode, label, icon: Icon }) => (
          <IconButton
            key={mode}
            title={label}
            active={interactionMode === "select" && transformMode === mode}
            disabled={!selectedId || interactionMode !== "select"}
            onClick={() => setTransformMode(mode)}
          >
            <Icon />
          </IconButton>
        ))}
      </div>

      <div className="icon-toolbar-divider" />

      <div className="icon-toolbar-group">
        <IconButton title="Rückgängig" disabled={pastLength === 0} onClick={() => undo()}>
          <UndoIcon />
        </IconButton>
        <IconButton title="Wiederholen" disabled={futureLength === 0} onClick={() => redo()}>
          <RedoIcon />
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({
  title,
  active,
  disabled,
  onClick,
  children,
}: {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`icon-btn ${active ? "active" : ""}`}
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
